import type { Node as RuntimeNode, Edge as RuntimeEdge } from '../../../core/models/scene';
import { getRelationshipMapping } from '../../../lib/diagram/registries';
import type { GraphEdge, GraphNode } from '../../../lib/graph/types';
import {
  attrsWithRelationSemantics,
  buildRelationSemantics,
  relationSemanticsForSubtype,
  storedRelationSemantics,
  stripRelationInternalAttrs,
} from '../../../lib/graph/relationshipSemantics';
import { getRelationPredicateDefinition } from '../../../core/ontology/RelationPredicateRegistry';
import type { OrgCustomFieldDefinition, OrgCustomFieldOverrideMap } from '../models/customFields';

export type { GraphEdge, GraphNode } from '../../../lib/graph/types';

/**
 * Org chart specific node props in universal format
 */
export interface OrgUniversalNodeProps {
  personId?: string;
  title?: string;
  department?: string;
  departmentId?: string;
  email?: string;
  joinDate?: string;
  departureDate?: string;
  photoRef?: string;
  level?: string;
  unstaffed?: boolean;
  parentDepartmentId?: string;
  code?: string;
  validFrom?: string;
  validTo?: string;
  customFieldOverrides?: OrgCustomFieldOverrideMap;
  [key: string]: unknown;
}

/**
 * Org chart specific edge props in universal format
 */
export interface OrgUniversalEdgeProps {
  dotted?: boolean;
  acting?: boolean;
  validFrom?: string;
  validTo?: string;
  [key: string]: unknown;
}

export type OrgNodeType = 'org_person' | 'org_position' | 'org_department' | 'org_team' | 'org_project' | 'org_role_profile';
export type OrgEdgeType =
  | 'reports_to' | 'occupies' | 'dotted_line_reports' | 'matrix_reports' | 'team_member' | 'assistant_to'
  | 'belongs_to' | 'heads' | 'located_at' | 'funded_by'
  | 'successor_of' | 'successor_for' | 'mentors' | 'assigned_to' | 'manages_project' | 'is_instance_of';

export interface OrgUniversalGraphSettings {
  orgchart?: {
    customFieldDefinitions?: OrgCustomFieldDefinition[];
  };
}

export interface OrgUniversalGraph {
  nodes: GraphNode<OrgNodeType, OrgUniversalNodeProps>[];
  edges: GraphEdge<OrgEdgeType, OrgUniversalEdgeProps>[];
  settings?: OrgUniversalGraphSettings;
}

/**
 * OrgChartHydrator interface for bidirectional mapping between
 * runtime canvas format and universal graph format.
 */
export interface OrgChartHydrator {
  /**
   * Convert runtime canvas nodes/edges to universal graph format.
   * Used for: export, AI integration, cross-domain analysis.
   */
  toUniversal: (
    nodes: RuntimeNode[],
    edges: RuntimeEdge[],
    settings?: OrgUniversalGraphSettings
  ) => OrgUniversalGraph;

  /**
   * Convert universal graph to runtime canvas format.
   * Used for: CSV import, AI-generated org charts.
   */
  fromUniversal: (graph: OrgUniversalGraph) => {
    nodes: RuntimeNode[];
    edges: RuntimeEdge[];
    settings?: OrgUniversalGraphSettings;
  };
}

function mapNodeTypeToUniversal(type: string): OrgNodeType {
  switch (type) {
    case 'person':
    case 'org_person':
      return 'org_person';
    case 'position':
    case 'role':
    case 'org_position':
      return 'org_position';
    case 'department':
    case 'org_department':
      return 'org_department';
    case 'team':
    case 'org_team':
      return 'org_team';
    case 'project':
    case 'org_project':
      return 'org_project';
    case 'role_profile':
    case 'org_role_profile':
      return 'org_role_profile';
    default:
      return 'org_position';
  }
}

function mapNodeTypeFromUniversal(type: OrgNodeType): string {
  switch (type) {
    case 'org_person':
      return 'org_person';
    case 'org_position':
      return 'org_position';
    case 'org_department':
      return 'org_department';
    case 'org_team':
      return 'org_team';
    case 'org_project':
      return 'org_project';
    case 'org_role_profile':
      return 'org_role_profile';
    default:
      return 'org_position';
  }
}

function isRuntimePersonType(type: string | undefined): boolean {
  return type === 'person'
    || type === 'org_person'
    || type === 'org_person_text'
    || type === 'org_person_photo'
    || type === 'org_person_photo_left'
    || type === 'org_person_photo_right'
    || type === 'org_person_photo_inner_left';
}

function isRuntimePositionType(type: string | undefined): boolean {
  return type === 'position' || type === 'role' || type === 'org_position';
}

function mapEdgeTypeToUniversal(type: string, attrs?: Record<string, unknown>): OrgEdgeType {
  switch (type) {
    case 'reporting':
      return 'reports_to';
    case 'dotted':
      return 'dotted_line_reports';
    case 'matrix':
      return 'matrix_reports';
    case 'team':
      return 'team_member';
    case 'occupies':
      return 'occupies';
    case 'belongs_to':
      return 'belongs_to';
    case 'heads':
      return 'heads';
    case 'located_at':
      return 'located_at';
    case 'funded_by':
      return 'funded_by';
    case 'successor_for':
    case 'successor_of':
      return 'successor_for';
    case 'mentors':
      return 'mentors';
    case 'assigned_to':
      return 'assigned_to';
    case 'manages_project':
      return 'manages_project';
    case 'is_instance_of':
      return 'is_instance_of';
    default:
      if (attrs?.dotted) return 'dotted_line_reports';
      return 'reports_to';
  }
}

function mapEdgeTypeFromUniversal(type: OrgEdgeType): string {
  switch (type) {
    case 'reports_to':
      return 'reporting';
    case 'dotted_line_reports':
      return 'dotted';
    case 'matrix_reports':
      return 'matrix';
    case 'team_member':
      return 'team';
    case 'occupies':
      return 'occupies';
    case 'assistant_to':
      return 'reporting';
    case 'belongs_to':
      return 'belongs_to';
    case 'heads':
      return 'heads';
    case 'located_at':
      return 'located_at';
    case 'funded_by':
      return 'funded_by';
    case 'successor_for':
    case 'successor_of':
      return 'successor_for';
    case 'mentors':
      return 'mentors';
    case 'assigned_to':
      return 'assigned_to';
    case 'manages_project':
      return 'manages_project';
    case 'is_instance_of':
      return 'is_instance_of';
    default:
      return 'reporting';
  }
}

export const orgChartHydrator: OrgChartHydrator = {
  toUniversal(nodes, edges, settings) {
    const runtimeNodesById = new Map(nodes.map((node) => [node.id, node]));
    const uNodes: GraphNode<OrgNodeType, OrgUniversalNodeProps>[] = nodes.map((n) => {
      const universalType = mapNodeTypeToUniversal(n.type);
      const attrs = n.attrs || {};
      const props = n.props || {};

      return {
        id: n.id,
        type: universalType,
        label: n.name || (attrs.name as string) || (attrs.title as string),
        props: {
          personId: attrs.personId as string,
          title: attrs.title as string,
          department: attrs.department as string,
          departmentId: attrs.departmentId as string,
          email: attrs.email as string,
          joinDate: attrs.joinDate as string,
          departureDate: attrs.departureDate as string,
          photoRef: attrs.photoRef as string,
          level: attrs.level as string,
          unstaffed: attrs.unstaffed as boolean,
          parentDepartmentId: attrs.parentDepartmentId as string,
          code: attrs.code as string,
          validFrom: props.validFrom as string,
          validTo: props.validTo as string,
          customFieldOverrides: attrs.customFieldOverrides as OrgCustomFieldOverrideMap,
          ...props,
        },
        tags: (attrs.tags as string[]) || [],
      };
    });

    const uEdges: GraphEdge<OrgEdgeType, OrgUniversalEdgeProps>[] = edges.map((e) => {
      const attrs = e.attrs || {};
      const cleanAttrs = stripRelationInternalAttrs(attrs);
      const universalType = mapEdgeTypeToUniversal(e.type, attrs);
      const props = {
        dotted: cleanAttrs.dotted as boolean,
        acting: cleanAttrs.acting as boolean,
        validFrom: cleanAttrs.validFrom as string,
        validTo: cleanAttrs.validTo as string,
        ...cleanAttrs,
      };
      const inferredSemantics = relationSemanticsForSubtype({
        diagramType: 'org',
        subtypeId: universalType,
        qualifierSource: props,
        fallbackPredicate: universalType,
        originalLabel: typeof attrs.relation === 'string' ? attrs.relation : e.type,
        existing: storedRelationSemantics(attrs),
      });
      const isSuccessorEdge = universalType === 'successor_of' || universalType === 'successor_for';
      const isInvalidSuccessorEndpoint = isSuccessorEdge
        && (!isRuntimePersonType(runtimeNodesById.get(e.from)?.type)
          || !isRuntimePositionType(runtimeNodesById.get(e.to)?.type));
      // The successor_for canvas subtype was removed from the relationship pack
      // (succession is authored through the protected panel), so the generic
      // subtype lookup can no longer promote these edges. Valid Person->Position
      // nominations hydrate to the canonical successor_of contract from the
      // predicate registry; anything else stays a demoted profile-only edge.
      const successorDefinition = isSuccessorEdge && !isInvalidSuccessorEndpoint
        ? getRelationPredicateDefinition('successor_of')
        : undefined;
      const semantics = isSuccessorEdge
        ? {
            ...(successorDefinition
              ? buildRelationSemantics({
                  ontology: {
                    predicate: successorDefinition.predicate,
                    status: successorDefinition.lifecycle,
                    facets: successorDefinition.facets ? [...successorDefinition.facets] : undefined,
                    qualifierKeys: successorDefinition.qualifierKeys
                      ? [...successorDefinition.qualifierKeys]
                      : undefined,
                  },
                  qualifierSource: props,
                  fallbackPredicate: 'successor_of',
                  existing: storedRelationSemantics(attrs),
                })
              : inferredSemantics),
            predicate: successorDefinition ? 'successor_of' : 'successor_for',
            status: successorDefinition
              ? ('projection_enabled' as const)
              : ('profile_only' as const),
            originalLabel: typeof attrs.relation === 'string' ? attrs.relation : e.type,
          }
        : inferredSemantics;

      return {
        id: e.id,
        type: universalType,
        from: e.from,
        to: e.to,
        props,
        time:
          cleanAttrs.validFrom || cleanAttrs.validTo
            ? {
                start: cleanAttrs.validFrom as string,
                end: cleanAttrs.validTo as string,
              }
            : undefined,
        semantics,
      };
    });

    return { nodes: uNodes, edges: uEdges, ...(settings ? { settings } : {}) };
  },

  fromUniversal(graph) {
    const nodes: RuntimeNode[] = graph.nodes.map((n, index) => {
      const runtimeType = mapNodeTypeFromUniversal(n.type);
      const {
        personId,
        title,
        department,
        departmentId,
        email,
        joinDate,
        departureDate,
        photoRef,
        level,
        unstaffed,
        parentDepartmentId,
        code,
        role,
        customFields,
        customFieldOverrides,
        ...restProps
      } = n.props;
      const isDepartment = n.type === 'org_department';
      const isPerson = n.type === 'org_person';
      const isTeam = n.type === 'org_team';
      const isProject = n.type === 'org_project';
      const isRoleProfile = n.type === 'org_role_profile';
      const resolvedName = (n.label as string) || (restProps.name as string) || undefined;
      const resolvedRole = (role as string) || (title as string) || undefined;

      const defaultDimensions = isTeam ? { w: 240, h: 70 }
        : isProject ? { w: 260, h: 90 }
        : isRoleProfile ? { w: 260, h: 90 }
        : isDepartment ? { w: 300, h: 200 }
        : { w: 200, h: 120 };

      return {
        id: n.id,
        type: runtimeType,
        category: 'orgchart',
        name: n.label,
        x: 100 + (index % 5) * 250,
        y: 100 + Math.floor(index / 5) * 180,
        ...defaultDimensions,
        attrs: {
          // Spread remaining ontology props first so explicit fields below take precedence
          ...restProps,
          personId,
          ...(isPerson ? { name: resolvedName, role: resolvedRole } : {}),
          title,
          department,
          departmentId,
          email,
          joinDate,
          departureDate,
          photoRef,
          level,
          unstaffed,
          customFieldOverrides,
          customFields: isPerson ? customFields : undefined,
          parentDepartmentId,
          code,
          ...((isDepartment || isTeam || isProject || isRoleProfile) ? { name: (n.props.name as string) || n.label } : {}),
          tags: n.tags,
        },
      };
    });

    const edges: RuntimeEdge[] = graph.edges.map((e) => {
      const runtimeType = mapEdgeTypeFromUniversal(e.type);
      const { dotted, acting, validFrom, validTo, ...restProps } = e.props;
      const subtypeId = e.type;
      const mapping = getRelationshipMapping('org', subtypeId);
      const defaultEdgeProps = (mapping?.defaultEdgeProps ?? {}) as Record<string, unknown>;
      const relation = defaultEdgeProps.relation ?? runtimeType;
      const anchors =
        runtimeType === 'reporting'
          ? { fromAnchor: 'top' as const, toAnchor: 'bottom' as const }
          : undefined;

      return {
        id: e.id,
        type: runtimeType as RuntimeEdge['type'],
        from: e.from,
        to: e.to,
        ...(anchors ?? {}),
        attrs: attrsWithRelationSemantics({
          ...defaultEdgeProps,
          dotted,
          acting,
          validFrom: e.time?.start || validFrom,
          validTo: e.time?.end || validTo,
          ...restProps,
          relation,
        }, e.semantics, subtypeId),
      };
    });

    return { nodes, edges, ...(graph.settings ? { settings: graph.settings } : {}) };
  },
};
